import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Tooltip from '../../Core/Tooltip';
import Label from '../../Core/InputLabel';
import Button from '../../Core/Button/Button';
import DialogCreateSkill from '../../Core/Dialog/CreateSkill';
import DialogCreateJob from '../../Core/Dialog/CreateJob';
import DialogCreateExpenseType from '../../Core/Dialog/CreateExpenseType';
import DialogCreatePlace from '../../Core/Dialog/CreatePlace';
import DialogCreateCustomCategory from '../../Core/Dialog/CreateCustomCategory';
import DialogCreateMaterial from '../../Core/Dialog/CreateMaterial';
import DialogCreateSupplier from '../../Core/Dialog/CreateSupplier';
import { createSkill, actionCreateJob, sendImportedPlacesSuccess, actionCreatePlace, actionCreateCustomCategory } from '../../../store/settings/actions';
import { importLoadingSelector, importedPlaces } from '../../../store/settings/selectors';
import { placesSelector } from '../../../store/places/selectors';
import { getPlaces } from '../../../store/places/actions';
import { createExpensesType } from '../../../store/expensesTypes/actions';
import { createMaterial } from '../../../store/materials/actions';
import { createSupplier } from '../../../store/suppliers/actions';

import ImportPlaces from 'components/Core/Dialog/ImportPlaces';
import _ from 'lodash';

export default function ButtonBlock({
  style, companyId,
  selectedCategory,
  setSelectedCategory,
  permissions,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [openNewItem, setOpenNewItem] = useState(false);
  const [openImportPlaces, setOpenImportPlaces] = useState(false);
  const importLoading = useSelector(importLoadingSelector);
  const allPlaces = useSelector(placesSelector);
  const imported = useSelector(importedPlaces);

  const clearImported = () => dispatch(sendImportedPlacesSuccess());

  const buttons = useMemo(() => {
    const data = [
      {
        onClick: () => setSelectedCategory('skills'),
        inverse: selectedCategory !== 'skills',
        title: 'Skill name',
      },
    ];

    if (permissions.create_jobs) {
      data.push({
        onClick: () => setSelectedCategory('jobs'),
        inverse: selectedCategory !== 'jobs',
        title: 'Job name',
      });
    }
    if (permissions.create_places) {
      data.push({
        onClick: () => setSelectedCategory('places'),
        inverse: selectedCategory !== 'places',
        title: 'Place name',
      });
    }
    if (permissions.custom_category) {
      data.push({
        onClick: () => setSelectedCategory('custom_category'),
        inverse: selectedCategory !== 'custom_category',
        title: 'Additional categories',
      });
    }
    if (permissions.use_expenses) {
      data.push({
        onClick: () => setSelectedCategory('expense_type'),
        inverse: selectedCategory !== 'expense_type',
        title: 'Expenses',
      });
    }
    if (permissions.use_expenses) {
      data.push({
        onClick: () => setSelectedCategory('customers'),
        inverse: selectedCategory !== 'customers',
        title: 'Customers',
      });
    }
    data.push({
      onClick: () => setSelectedCategory('materials'),
      inverse: selectedCategory !== 'materials',
      title: 'Materials',
    });
    data.push({
      onClick: () => setSelectedCategory('suppliers'),
      inverse: selectedCategory !== 'suppliers',
      title: 'Suppliers',
    });

    return data;
  }, [permissions, selectedCategory, setSelectedCategory]);
  const itemName = useMemo(() => {
    switch (selectedCategory) {
      case 'skills': {
        return 'skill';
      }
      case 'jobs': {
        return 'job';
      }
      case 'places': {
        return 'place';
      }
      case 'custom_category': {
        return 'additional category';
      }
      case 'customers': {
        return 'customer';
      }
      case 'expense_type': {
        return 'expense type';
      }
      case 'materials': {
        return 'material';
      }
      case 'suppliers': {
        return 'supplier';
      }
      default: return '';
    }
  }, [selectedCategory]);

  const handleCloseItem = () => {
    setOpenNewItem(false);
  };

  const createNewSkill = (values) => {
    dispatch(createSkill({
      ...values,
      use_rates: Number(values.use_rates),
    }, companyId));
    handleCloseItem();
  };
  const createJob = (values) => {
    dispatch(actionCreateJob(values, companyId));
    handleCloseItem();
  };
  const createExpenseType = (values) => {
    dispatch(createExpensesType(companyId, values));
    handleCloseItem();
  }
  const createMaterialDetails = (values) => {
    dispatch(createMaterial(companyId, values));
    handleCloseItem();
  };
  const createSupplierDetails = (values) => {
    dispatch(createSupplier(companyId, values));
    handleCloseItem();
  };
  const handleCreateCustomer = () => {
    history.push(`/${companyId}/settings/categories/customers/create`);
  }
  const createPlace = ({name, external_id, type}) => {
    dispatch(actionCreatePlace({ name, external_id, type }, companyId));
    handleCloseItem();
  };
  const createCustomCategory = ({name, field_type, dropdown_fields, start_work, finish_work}) => {
    dispatch(actionCreateCustomCategory({ name, field_type, start_work, finish_work, dropdown_fields }, companyId));
    handleCloseItem();
  };

  return (
    <div className={style.categoryPage__Button}>
      <div className={style.labelBlock}>
        <Label text={t('Select Category')} htmlFor='new_skill' />
        <Tooltip title={t('Select Category')} />
      </div>
      {
        buttons.map((button, index) => (
          <Button
            key={index+'-button'}
            onClick={button.onClick}
            inverse={button.inverse}
            fillWidth
            size='big'
          >
            {t(button.title)}
          </Button>
        ))
      }
      <div className={style.newSkillBlock}>
        <Label text={t(`New ${itemName}`)} htmlFor={`new_${itemName}`} />
        <Button
          onClick={selectedCategory === 'customers' ? handleCreateCustomer : setOpenNewItem}
          white
          fillWidth
          size='big'
        >
          {t(`Create new ${itemName}`)}
        </Button>
        {selectedCategory === 'places' && (
          <Button
            onClick={() => setOpenImportPlaces(true)}
            white
            fillWidth
            size='big'
          >
            {t(`Import places`)}
          </Button>
        )}
      </div>
      <DialogCreateSkill
        open={openNewItem && selectedCategory === 'skills'}
        handleClose={handleCloseItem}
        title={t('Create new skill')}
        buttonTitle={t('Create new skill')}
        createSkill={createNewSkill}
        permissions={permissions}
      />
      <DialogCreateJob
        open={openNewItem && selectedCategory === 'jobs'}
        handleClose={handleCloseItem}
        title={t('Create Job name')}
        buttonTitle={t('Create Job Name')}
        createJob={createJob}
        permissions={permissions}
      />
      <DialogCreateExpenseType
        open={openNewItem && selectedCategory === 'expense_type'}
        handleClose={handleCloseItem}
        title={t('Expense type')}
        buttonTitle={t('Create expense type')}
        createJob={createExpenseType}
        permissions={permissions}
      />
      <DialogCreatePlace
        open={openNewItem && selectedCategory === 'places'}
        handleClose={handleCloseItem}
        title={t('Create Place name')}
        buttonTitle={t('Create Place Name')}
        createPlace={createPlace}
        permissions={permissions}
      />
      <DialogCreateCustomCategory
        open={openNewItem && selectedCategory === 'custom_category'}
        handleClose={handleCloseItem}
        title={t('Create additional category')}
        buttonTitle={t('Create Additional Category')}
        createCategory={createCustomCategory}
      />
      <DialogCreateMaterial
        open={openNewItem && selectedCategory === 'materials'}
        handleClose={handleCloseItem}
        title={t('Create material')}
        buttonTitle={t('Create material')}
        saveMaterial={createMaterialDetails}
      />
      <DialogCreateSupplier
        open={openNewItem && selectedCategory === 'suppliers'}
        handleClose={handleCloseItem}
        title={t('Create supplier')}
        buttonTitle={t('Create supplier')}
        saveSupplier={createSupplierDetails}
      />
      <ImportPlaces
        title={t('Import places')}
        open={openImportPlaces}
        handleClose={() => {
          if (importLoading) {
            return;
          }

          setOpenImportPlaces(false);
          if (!_.isEmpty(imported)) {
            dispatch(getPlaces(companyId));
          }
        }}
        imported={imported}
        clearImported={clearImported}
        places={allPlaces}
        loading={importLoading}
      />

    </div>
  );
}
